import { useEffect } from 'react';
import { Form } from 'antd';
import { Assistant } from '../../../contexts/AssistantContext';
import { getResponseLength } from '../utils';
import { AdvancedConfig, GuardrailConfig } from '../types';
import { DEFAULT_GUARDRAILS } from '../constants';

import { createLogger } from '../../../utils/logger';
const logger = createLogger('features-assistants');

interface UseMyAssistantPersonalityProps {
  mode: 'edit' | 'create';
  currentAssistant: Assistant | null;
  form: ReturnType<typeof Form.useForm>[0];
  setAdvancedConfig?: (config: AdvancedConfig) => void;
}

const buildGuardrailsFromBackend = (guardrailsConfig: { inputGuards?: string[]; outputGuards?: string[] } | undefined): GuardrailConfig[] => {
  if (!guardrailsConfig) {
    return DEFAULT_GUARDRAILS;
  }

  const allEnabledGuards = [...(guardrailsConfig.inputGuards || []), ...(guardrailsConfig.outputGuards || [])];
  const uniqueEnabledGuards = [...new Set(allEnabledGuards)];

  return DEFAULT_GUARDRAILS.map(guard => ({
    ...guard,
    enabled: uniqueEnabledGuards.includes(guard.id)
  }));
};

export const useMyAssistantPersonality = ({
  mode,
  currentAssistant,
  form,
  setAdvancedConfig,
}: UseMyAssistantPersonalityProps) => {
  useEffect(() => {
    logger.info('[useMyAssistantPersonality] useEffect triggered');
    logger.info('[useMyAssistantPersonality] mode:', mode);
    logger.info('[useMyAssistantPersonality] currentAssistant:', currentAssistant);
    logger.info('[useMyAssistantPersonality] currentAssistant has keys:', currentAssistant ? Object.keys(currentAssistant).length : 0);

    if (mode === 'edit' && currentAssistant && Object.keys(currentAssistant).length > 0) {
      logger.info('[useMyAssistantPersonality] ✅ ENTERING CONDITION - Will set form values');
      logger.info('Loading assistant data:', currentAssistant);
      logger.info('Assistant advancedConfig:', currentAssistant.advancedConfig);
      logger.info('Assistant guardrailsConfig:', currentAssistant.guardrailsConfig);
      logger.info('Assistant businessRules:', currentAssistant.businessRules);
      
      // Build guardrails configuration from backend data
      const guardrailsFromBackend = buildGuardrailsFromBackend(currentAssistant.guardrailsConfig);
      logger.info('Built guardrails from backend:', guardrailsFromBackend);
      
      const advancedConfigData: AdvancedConfig = {
        guardrails: guardrailsFromBackend,
        businessRules: currentAssistant.advancedConfig?.businessRules || []
      };
      
      const currentValues = form.getFieldsValue();
      const newValues = {
        agentName: currentAssistant.name,
        businessName: currentAssistant.businessName,
        communicationStyle: currentAssistant.communicationStyle || 'neutral',
        responseLength: getResponseLength(currentAssistant.openAIConfig?.max_tokens),
        voiceLanguage: currentAssistant.voiceLanguage || 'off',
        domainTopics: currentAssistant.domainTopic,
        chatTopics: currentAssistant.chatTopics || '',
        role: currentAssistant.specialist,
        fallbackResponse: currentAssistant.fallbackResponse,
        selectedModel: currentAssistant.openAIConfig?.model || 'gpt-4o',
        modelApiKey: currentAssistant.openAIConfig?.api_key || '',
        whatsappMode: currentAssistant.whatsappMode || undefined,
        advancedConfig: {
          ...advancedConfigData,
          acronyms: currentAssistant.advancedConfig?.acronyms || [],
        },
      };

      logger.info('Setting form values:', newValues);
      logger.info('Current form values BEFORE setting:', currentValues);

      // Only update if values have changed
      if (JSON.stringify(currentValues) !== JSON.stringify(newValues)) {
        logger.info('✅ Values are different, calling form.setFieldsValue');
        form.setFieldsValue(newValues);

        // Verify values were set
        setTimeout(() => {
          const valuesAfterSet = form.getFieldsValue();
          logger.info('Form values AFTER setting:', valuesAfterSet);
          logger.info('agentName after set:', valuesAfterSet.agentName);
          logger.info('businessName after set:', valuesAfterSet.businessName);
        }, 100);
      } else {
        logger.warn('⚠️ Values are the same, skipping form.setFieldsValue');
      }

      // Update advancedConfig state if setter is provided
      if (setAdvancedConfig) {
        logger.info('Setting advancedConfig state:', advancedConfigData);
        setAdvancedConfig(advancedConfigData);
      }
    } else {
      logger.warn('[useMyAssistantPersonality] ❌ NOT ENTERING CONDITION');
      logger.warn('[useMyAssistantPersonality] Reason - mode:', mode, 'is edit?', mode === 'edit');
      logger.warn('[useMyAssistantPersonality] Reason - currentAssistant:', !!currentAssistant);
      logger.warn('[useMyAssistantPersonality] Reason - has keys?', currentAssistant ? Object.keys(currentAssistant).length > 0 : false);

      // Set default model for create mode
      if (mode === 'create') {
        form.setFieldsValue({ selectedModel: 'gpt-4o' });
      }
    }
  }, [mode, currentAssistant, form, setAdvancedConfig]); // Fixed: Use full currentAssistant object instead of just id to properly detect data updates
};
